import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/pages/auth/api";
import {
  clearSession,
  getStoredSessionToken,
  getStoredUsername,
} from "@/pages/auth/storage";
import type {
  UserPrivateProfile,
  UserPublicProfile,
} from "@/pages/profile/api";
import { getOwnProfile, getPublicProfile } from "@/pages/profile/api";
import { ProfileActivityPanel } from "@/pages/profile/ProfileActivityPanel";
import { ProfileAchievements } from "@/pages/profile/ProfileAchievements";
import { ProfileIdentityChips } from "@/pages/profile/ProfileIdentityChips";
import { initialsForUsername } from "@/pages/profile/formatters";
import { useAuthSession } from "@/shared/auth/useAuthSession";
import { useI18n } from "@/shared/i18n/LanguageContext";

type ProfileData = UserPublicProfile | UserPrivateProfile;

export function ProfilePage() {
  const navigate = useNavigate();
  const { copy } = useI18n();
  const { username = "username" } = useParams();
  const authSession = useAuthSession();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "not-found" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const storedUsername = getStoredUsername();
  const hasSessionToken = Boolean(getStoredSessionToken());

  const isOwnProfile =
    hasSessionToken &&
    (storedUsername ?? authSession.username)?.toLowerCase() === username.toLowerCase();

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError(null);

    const load = async () => {
      try {
        if (isOwnProfile) {
          const token = getStoredSessionToken();
          if (!token) {
            throw new Error("missing session token");
          }
          const ownProfile = await getOwnProfile(token);
          if (cancelled) {
            return;
          }
          setProfile(ownProfile);
          setStatus("ready");
          return;
        }

        const publicProfile = await getPublicProfile(username);
        if (cancelled) {
          return;
        }
        setProfile(publicProfile);
        setStatus("ready");
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        if (loadError instanceof ApiError && loadError.status === 404) {
          setStatus("not-found");
          return;
        }
        if (isOwnProfile && loadError instanceof ApiError && loadError.status === 401) {
          clearSession();
          try {
            const publicProfile = await getPublicProfile(username);
            if (cancelled) {
              return;
            }
            setProfile(publicProfile);
            setStatus("ready");
            return;
          } catch (fallbackError) {
            if (fallbackError instanceof ApiError && fallbackError.status === 404) {
              setStatus("not-found");
              return;
            }
            setError(fallbackError instanceof Error ? fallbackError.message : copy.profile.failed);
            setStatus("error");
            return;
          }
        }
        setError(loadError instanceof Error ? loadError.message : copy.profile.failed);
        setStatus("error");
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [copy.profile.failed, isOwnProfile, username]);

  if (status === "loading") {
    return (
      <div className="page-stack">
        <section className="profile-view">
          <article className="profile-hero">
            <p className="profile-status">{copy.profile.loading}</p>
          </article>
        </section>
      </div>
    );
  }

  if (status === "not-found") {
    return (
      <div className="page-stack">
        <section className="profile-view">
          <article className="profile-hero">
            <h1 className="profile-name">@{username}</h1>
            <p className="profile-status">{copy.profile.notFound}</p>
          </article>
        </section>
      </div>
    );
  }

  if (!profile || status === "error") {
    return (
      <div className="page-stack">
        <section className="profile-view">
          <article className="profile-hero">
            <h1 className="profile-name">@{username}</h1>
            <p className="profile-status">{error ?? copy.profile.failed}</p>
          </article>
        </section>
      </div>
    );
  }

  const hasBio = Boolean(profile.bio?.trim());
  const canEditProfile = isOwnProfile && "email_verified" in profile;

  return (
    <div className="page-stack">
      <section className="profile-view">
        <article className="profile-hero">
          <div className="profile-hero__head">
            {profile.avatar_url ? (
              <img
                className="profile-hero__avatar-image"
                src={profile.avatar_url}
                alt={`@${profile.username}`}
              />
            ) : (
              <div className="profile-hero__avatar-fallback" aria-hidden="true">
                {initialsForUsername(profile.username)}
              </div>
            )}

            <div className="profile-hero__identity">
              <h1 className="profile-name">@{profile.username}</h1>
              <ProfileIdentityChips profile={profile} />
            </div>

            {canEditProfile ? (
              <button
                className="button-link button-link--secondary"
                type="button"
                onClick={() => navigate("/settings/profile")}
              >
                {copy.profile.editProfile}
              </button>
            ) : null}
          </div>
        </article>

        {hasBio ? (
          <article className="profile-section">
            <h2>{copy.profile.bio}</h2>
            <p>{profile.bio}</p>
          </article>
        ) : null}

        <ProfileAchievements profile={profile} />

        <ProfileActivityPanel profile={profile} />
      </section>
    </div>
  );
}
